/**
 * 재위탁 업체 등록·상세 공통 옵션.
 * Figma 업체 기본 정보 / 정산·초기 계약 / 첨부 서류 카드와 맞춘다.
 */
import type {
  PartnerCompanyContract,
  PartnerCompanyDocumentType,
  PartnerCompanyType,
} from '@/entities/partner-company/api/partner-company.api';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { cn } from '@/shared/lib/utils';
import React from 'react';

export const SETTLEMENT_CYCLE_OPTIONS = [
  { value: 'MONTHLY', label: '월 단위' },
] as const;

/** 루다링크만 내부 유형. 그 외 업체명은 전부 외부 */
export const RUDALINK_COMPANY_NAME = '루다링크';

export const resolvePartnerCompanyType = (
  companyName: string,
): PartnerCompanyType =>
  companyName.trim() === RUDALINK_COMPANY_NAME ? 'RUDALINK' : 'EXTERNAL';

export const PARTNER_COMPANY_DOCUMENT_SLOTS = [
  { type: 'CONTRACT', label: '계약서' },
  { type: 'BUSINESS_LICENSE', label: '사업자등록증' },
  { type: 'CSO_REPORT', label: '판촉영업자신고증' },
  { type: 'BANKBOOK', label: '통장 사본' },
] as const satisfies ReadonlyArray<{
  type: PartnerCompanyDocumentType;
  label: string;
}>;

export type PartnerCompanyDocumentSlotType =
  (typeof PARTNER_COMPANY_DOCUMENT_SLOTS)[number]['type'];

export const getSettlementCycleLabel = (value?: string | null) =>
  SETTLEMENT_CYCLE_OPTIONS.find((option) => option.value === value)?.label ??
  value ??
  '';

export const toPartnerCompanyContractDate = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .slice(0, 10);

export const getLatestPartnerCompanyContract = (
  contracts: PartnerCompanyContract[],
) =>
  [...contracts].sort((left, right) => right.contractId - left.contractId)[0];

/** 계약 PATCH/POST 바디. 종료일이 비면 시작일과 같게 보낸다. */
export const buildPartnerCompanyContractPeriodBody = (
  startDate: string,
  endDate: string,
) => {
  const start = toPartnerCompanyContractDate(startDate);
  const end = toPartnerCompanyContractDate(endDate) || start;
  return { startDate: start, endDate: end };
};

export const didPartnerCompanyContractPeriodChange = (
  contract: PartnerCompanyContract | undefined,
  startDate: string,
  endDate: string,
) => {
  const next = buildPartnerCompanyContractPeriodBody(startDate, endDate);
  if (!next.startDate) return false;
  if (!contract) return true;
  return (
    toPartnerCompanyContractDate(contract.startDate) !== next.startDate ||
    toPartnerCompanyContractDate(contract.endDate) !== next.endDate
  );
};

export const PartnerCompanyDocumentSlotRow: React.FC<{
  label: string;
  fileName?: string | null;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
}> = ({ label, fileName, disabled, onFileSelected }) => (
  <div className="flex items-center gap-3 rounded-md border border-dashed border-border px-3 py-2">
    <p className="w-[88px] shrink-0 text-sm text-muted-foreground sm:w-[120px]">
      {label}
    </p>
    <p
      className={cn(
        'min-w-0 flex-1 truncate text-sm',
        fileName ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      {fileName || '선택된 파일 없음'}
    </p>
    <FileUploadButton
      accept=".pdf,.jpg,.jpeg,.png"
      disabled={disabled}
      showUploadIcon={false}
      resetOnSelect={false}
      onFilesSelected={(files) => {
        const file = files?.[0];
        if (file) onFileSelected(file);
      }}
    >
      파일 선택
    </FileUploadButton>
  </div>
);
